import { IUnitDetailsDto, RolesType, UnitDetailsDto } from 'api/api';
import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { notificationService } from 'services/notification-service';
import { createFavourite, deleteFavourite } from 'state/ducks/favourite-unit/operations';
import { getFilterOptions } from 'state/ducks/filter-search/operations';
import {
	UpdateIsFavourite,
	UpsertMachineDetails,
	UpsertMachineDetailsBasicUser,
} from 'state/ducks/machine-details/operations';
import { localized } from 'state/i18n';
import { AppState } from 'state/store';
import { getMachineFromIdDataMapOrDefault } from 'utilities/machine-util';
import { hasAtLeastPermissionSuperAdmin, isAtLeastCountryAdminFromState } from 'utilities/roles-util';
import { getEmailFromToken } from 'utilities/token-util';
import WhiteCard from 'view/shared/components/cards/white-card';
import LabelValueCard from 'view/shared/components/label-value-card';
import { cardDetailsStyles, cardGeneralClassNames } from '../constants';
import { ManualItem } from '../documents-helper';
import { DeviceDocumentButtons } from './device-document-buttons';
import { getTechnicalDetailsPairValues } from './technical-details-helper';
import './technical-details.scss';

const mapStateToProps = (state: AppState, ownProps: TechnicalDetailsAppProps) => {
	const currMachine = getMachineFromIdDataMapOrDefault(
		state.machineDetailsReducer.machineDetails,
		ownProps.machineDetailsId,
		UnitDetailsDto
	);

	const userGroups = state.groupsReducer.groups.find(g => g.id === getEmailFromToken());
	const userRole =
		userGroups?.data.find(g => g.id === currMachine.data.ownerGroupId)?.roleType ?? RolesType.DataOutOfRange;
	const isCountryAdmin = isAtLeastCountryAdminFromState(state, currMachine.data.ownerGroupId);

	return {
		machineDetailsDto: currMachine.data,
		pickupSystem: state.machineDetailsReducer.options.pickupSystems,
		manufacturer: state.machineDetailsReducer.options.manufacturers,
		unitModels: state.machineDetailsReducer.options.unitModels,
		unitTypes: state.machineDetailsReducer.options.unitTypes,
		wasteTypes: state.machineDetailsReducer.options.wasteTypes,
		containerSizes: state.machineDetailsReducer.options.containerSizes,
		filterOptions: state.filterSearchReducer.filterOptions,
		unitModelId: currMachine.data.unitModelId,
		unitTypeId: currMachine.data.unitTypeId,
		wasteTypeId: currMachine.data.wasteTypeId,
		lastestChange: currMachine.data.lastestChange,
		containerSize: currMachine.data.containerSize,
		manufacturerId: currMachine.data.manufacturerId!,
		pickupSystemId: currMachine.data.unitPickupSystemId,
		totalHourCount: currMachine.data.totalRunningHours,
		totalNumberOfCycles: currMachine.data.totalNumberOfCycles,
		cyclesSinceLastOutput: currMachine.data.cyclesSinceLastOutput,
		totalNumberOfpressSeries: currMachine.data.totalNumberOfPressSeries,
		pressSeriesSinceLastOutput: currMachine.data.pressSeriesSinceLastOutput,
		unitTracker: state.deviceTrackerReducer.unitTrackers.find(tracker => tracker.unitId === currMachine.data.id),
		devicePhoneNo: currMachine.data.devicePhoneNo,
		machineGroupsName: currMachine.data.unitTypeName,
		ownerGroupId: currMachine.data.ownerGroupId,
		pricePerEmptying: currMachine.data.pricePerEmptying,
		pricePerEmptyingCurrency: currMachine.data.pricePerEmptyingCurrency,
		optimizationProjectEnabledFor: currMachine.data.optimizationProjectEnabledFor,
		isFavourite: currMachine.data.isFavourite,
		isCountryAdmin,
		isSuperAdmin: state.userPermissionReducer.UserGroupPermissions.some(ugp =>
			hasAtLeastPermissionSuperAdmin(ugp.roleType)
		),
		userRole,
		emptyCounter: currMachine.data.emptyCounter,
		isBleTag: currMachine.data.isBleTag,
		isSwapContainer: currMachine.data.isSwapContainer,
		connectedBleTagMac: currMachine.data.connectedBleTagMac,
		connectedContainerSerialNumber: currMachine.data.connectedContainerSerialNumber,
		fieldsFromConnectedBleTag: currMachine.data.fieldsFromConnectedBleTag,
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
	UpsertMachineDetails: async (unitDetail: IUnitDetailsDto) => (await UpsertMachineDetails(unitDetail))(dispatch),
	UpsertMachineDetailsBasicUser: async (unitDetail: IUnitDetailsDto) =>
		(await UpsertMachineDetailsBasicUser(unitDetail))(dispatch),
	getFilterOptions: async () => (await getFilterOptions())(dispatch),
	createFavourite: async (unitDetail: IUnitDetailsDto) => {
		(await createFavourite(unitDetail.id))(dispatch);
		UpdateIsFavourite(unitDetail, true)(dispatch);
	},
	deleteFavourite: async (unitDetail: IUnitDetailsDto) => {
		(await deleteFavourite(unitDetail.id))(dispatch);
		UpdateIsFavourite(unitDetail, false)(dispatch);
	},
});

export interface TechnicalDetailsAppProps {
	machineDetailsId: string;
	isCountryAdmin: boolean;
	isAdmin: boolean;
	handleVideoBtnPressed: (videoName: string) => void;
	manualItems: ManualItem[];
}

export type TechnicalDetailsProps = ReturnType<typeof mapStateToProps> &
	ReturnType<typeof mapDispatchToProps> &
	TechnicalDetailsAppProps;

class TechnicalDetails extends React.PureComponent<TechnicalDetailsProps> {
	public render() {
		const technicalDetailsPairValues = getTechnicalDetailsPairValues(this.props, {
			toggleIsFavourite: this.toggleIsFavourite,
			saveItem: this.saveItem,
			saveItemBasicUser: this.saveItemBasicUser,
		});
		const containerClassName = `${cardGeneralClassNames.classNameContainer} ${cardDetailsStyles.classNameContainer}`;

		return (
			<WhiteCard title={localized('TechnicalDetails')} classNameContainer={containerClassName}>
				<LabelValueCard
					labels={technicalDetailsPairValues.labels}
					values={technicalDetailsPairValues.valuesForLabels}
				/>

				<DeviceDocumentButtons
					machineDetailsId={this.props.machineDetailsId}
					handleVideoBtnPressed={this.props.handleVideoBtnPressed}
					manualItems={this.props.manualItems}
				/>
			</WhiteCard>
		);
	}

	public async componentDidMount() {
		if (!this.props.filterOptions.machineTypeOptions.length) {
			await this.props.getFilterOptions();
		}

		this.setState({ selectedGroupNameId: this.props.ownerGroupId });
	}

	private saveItem = async <K extends keyof IUnitDetailsDto>(key: K, value: IUnitDetailsDto[K]) => {
		let machineDetailsDto = { ...this.props.machineDetailsDto } as IUnitDetailsDto;
		if (key === 'unitModelId' && this.props.unitModels) {
			const unitModel = this.props.unitModels.find(unitModel => unitModel.id === value);
			if (unitModel) {
				machineDetailsDto.manufacturerId = unitModel.unitManufacturerId;
				machineDetailsDto.unitTypeId = unitModel.unitTypeId;
			}
		}
		machineDetailsDto[key] = value;
		await this.props.UpsertMachineDetails(machineDetailsDto);
	};

	/**
	 * Only set values which a basic user can set. Otherwise, the API can throw an auth exception
	 * @param key
	 * @param value
	 */
	private saveItemBasicUser = async <K extends keyof IUnitDetailsDto>(
		key: K,
		value: IUnitDetailsDto[K],
		valueType: 'string' | 'number' = 'string'
	) => {
		const machineDetailsDto: IUnitDetailsDto = { ...this.props.machineDetailsDto };

		if (valueType === 'number' && isNaN(Number(value))) {
			notificationService.showWarningMessage(localized('TheInputIsInvalid'));
			return;
		}

		machineDetailsDto[key] = value;
		await this.props.UpsertMachineDetailsBasicUser(machineDetailsDto);
	};

	private toggleIsFavourite = () => {
		this.props.isFavourite
			? this.props.deleteFavourite(this.props.machineDetailsDto)
			: this.props.createFavourite(this.props.machineDetailsDto);
	};
}

export default connect(mapStateToProps, mapDispatchToProps)(TechnicalDetails);
